/**
 * Persistent auto-save status pill (plan D8 / spec "Editor auto-save").
 * Shows one of: Saved · just now / Saving… / Retrying… / Offline — changes
 * paused. On a paused failure it surfaces the non-blocking error with
 * [Retry] and [Dismiss] inline (spec: "Couldn't save that change — [Retry]").
 *
 * Editorial restraint: Geist, muted ink, near the itinerary title.
 */

import type { AutoSaveStatus } from './useItineraryMutationQueue';
import { Caption } from '../ui/Typography';
import { Button } from '../ui/Button';

interface AutoSaveStatusPillProps {
  status: AutoSaveStatus;
  failure: { label: string; message: string } | null;
  onRetry: () => void;
  onDismiss: () => void;
}

const LABEL: Record<AutoSaveStatus, string> = {
  saved: 'Saved · just now',
  saving: 'Saving…',
  retrying: 'Retrying…',
  offline: 'Offline — changes paused',
};

export const AutoSaveStatusPill = ({
  status,
  failure,
  onRetry,
  onDismiss,
}: AutoSaveStatusPillProps) => {
  const dotClass =
    status === 'saved'
      ? 'bg-success-500'
      : status === 'offline'
        ? 'bg-warning'
        : status === 'retrying'
          ? 'bg-error'
          : 'bg-gray-400';

  return (
    <div
      className="flex flex-col gap-2"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="inline-flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${dotClass} ${
            status === 'saving' || status === 'retrying'
              ? 'motion-safe:animate-pulse'
              : ''
          }`}
          aria-hidden="true"
        />
        <Caption className="text-gray-600">{LABEL[status]}</Caption>
      </div>

      {failure && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#DBD4C9] bg-[#F2F0EA] px-3 py-2">
          <Caption className="text-error">
            Couldn&apos;t save that change ({failure.label})
          </Caption>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRetry}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
